import React, { useState, useEffect } from 'react';
import { Input } from '../components/ui/input';

/**
 * MoneyInput — formatted number input for DZD amounts.
 * Displays digits with non-breaking thousand separators ("500 000")
 * Stores the raw numeric value in parent state.
 *
 * Props:
 *  - value   : number | string (raw numeric value from parent state)
 *  - onChange: (rawNumber: number | '') => void
 *  - rest    : passed through to the underlying <Input>
 *
 * Notes:
 *  - Uses type="text" + inputMode="numeric" so the formatting can render.
 *  - Strips anything that isn't a digit (no decimals — DZD is integer).
 *  - Returns '' (empty string) when the field is cleared so parent can detect empty state.
 */
const formatGrouped = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return '';
  const digits = String(raw).replace(/[^0-9]/g, '');
  if (!digits) return '';
  // Insert a thin/regular space every 3 digits from the right
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const MoneyInput = ({ value, onChange, className = '', ...rest }) => {
  const [display, setDisplay] = useState(formatGrouped(value));

  // Keep local display in sync when parent value changes externally
  useEffect(() => {
    const incoming = formatGrouped(value);
    // Only overwrite when the digits actually differ (avoids cursor jumps mid-edit)
    const currentDigits = display.replace(/\s/g, '');
    const incomingDigits = incoming.replace(/\s/g, '');
    if (incomingDigits !== currentDigits) {
      setDisplay(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setDisplay(formatGrouped(raw));
    if (typeof onChange === 'function') {
      onChange(raw === '' ? '' : Number(raw));
    }
  };

  return (
    <Input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      value={display}
      onChange={handleChange}
      className={`font-mono tabular-nums ${className}`}
      {...rest}
    />
  );
};

export default MoneyInput;
